import Backbone from '@trello/backbone';
import { googleDriveClientId } from '@trello/config';
import { loadScript } from '@trello/dom';
import type { TrelloWindow } from '@trello/window-types';

declare const window: TrelloWindow;

export class GoogleApi {
  static loaded = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static oauthToken: any | null = null;
  static async ensureApiLoaded() {
    if (!GoogleApi.loaded) {
      const loadApi = loadScript('https://apis.google.com/js/api.js');
      const loadClient = loadScript('https://accounts.google.com/gsi/client');

      await loadApi;
      await loadClient;

      GoogleApi.loadedAuth();
    }
  }

  static loadedAuth() {
    // If the script is loaded, gapi should be in the window scope
    // If it isn't, then something went wrong (perhaps it was blocked by an
    // extension or something)
    return window.gapi?.load('client', () => {
      GoogleApi.loaded = true;
      GoogleApi.trigger('loaded');
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static authorize(_immediate: any, next: any) {
    // If we have a valid token, use it
    if (GoogleApi.validToken()) {
      return typeof next === 'function'
        ? next(null, GoogleApi.oauthToken.access_token)
        : undefined;
    }

    const tokenClient = window.google?.accounts.oauth2.initTokenClient({
      client_id: googleDriveClientId,
      scope: 'https://www.googleapis.com/auth/drive.file',
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tokenClient.callback = (result: any) => {
      if (!result || result.error) {
        GoogleApi.oauthToken = null;
        return typeof next === 'function'
          ? next(result?.error ?? Error('Failed to request token'))
          : undefined;
      }
      GoogleApi.oauthToken = result;
      // Tokens via GIS don't have expires_at so we have to set it ourselves
      GoogleApi.oauthToken.expires_at =
        Date.now() / 1000 + parseInt(GoogleApi.oauthToken.expires_in, 10);
      return typeof next === 'function'
        ? next(null, GoogleApi.oauthToken.access_token)
        : undefined;
    };

    // This will show the consent screen the first time and a loading screen after
    return tokenClient.requestAccessToken({ prompt: '' });
  }

  static validToken() {
    // Token must exist and not have expired
    return (
      GoogleApi.oauthToken?.access_token &&
      // eslint-disable-next-line radix
      Date.now() / 1000 < parseInt(GoogleApi.oauthToken.expires_at)
    );
  }

  static off = Backbone.Events.off;
  static on = Backbone.Events.on;
  static trigger = Backbone.Events.trigger;
}
